import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { rankSalePicks } from './br-sale-picks-core.js';
import { rankPriorityCollection } from './br-sale-picks-priority.js';

let generation = 0;
let pendingMutations = 0;
let dirty = true;
let activeRequest;
let savedView;
let previousIds = [];
let preserveOrder = false;
let loading = false;
const mounts = new Set();

const cartSignature = (lines) => JSON.stringify([...lines].sort((a, b) => String(a.key).localeCompare(String(b.key))));

async function fetchSection(rawUrl, sectionId, signal, page) {
  const url = new URL(rawUrl, location.origin);
  if (url.origin !== location.origin) throw new Error('CROSS_ORIGIN_SECTION');
  url.search = '';
  url.searchParams.set('section_id', sectionId);
  if (page) url.searchParams.set('page', String(page));
  const response = await fetch(url, { signal, credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error('SECTION_UNAVAILABLE');
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

function parsePayload(doc, selector) {
  const node = doc.querySelector(selector);
  if (!node) throw new Error('SECTION_PAYLOAD_MISSING');
  return JSON.parse(node.dataset.json);
}

function applyEligiblePrice(slide, candidate) {
  const price = slide.querySelector('[data-br-sale-picks-price]');
  const templates = [...slide.querySelectorAll('template[data-br-sale-picks-variant-price]')];
  const compareTemplates = [...slide.querySelectorAll('template[data-br-sale-picks-variant-compare-price]')];
  const range = candidate.priceRange;
  const formatted = (id, amount, compare = false) => {
    const source = compare ? compareTemplates : templates;
    const matches = source.filter((node) => node.dataset.variantId === id
      && Number(compare ? node.dataset.compareAtPrice : node.dataset.price) === amount);
    if (matches.length !== 1 || !matches[0].content.textContent.trim()) throw new Error('CARD_PRICE_INCOMPLETE');
    const amountNode = document.createElement('span');
    if (!compare) amountNode.className = 'price';
    amountNode.append(matches[0].content.cloneNode(true));
    return amountNode;
  };
  if (!price || !range) throw new Error('CARD_PRICE_INCOMPLETE');
  const label = document.createElement('span');
  label.className = 'visually-hidden';
  label.textContent = `${range.min === range.max ? price.dataset.singleLabel : price.dataset.rangeLabel} `;
  price.replaceChildren(label, formatted(range.minVariantId, range.min));
  if (range.min !== range.max) price.append(' – ', formatted(range.maxVariantId, range.max));
  const eligibleIds = new Set(candidate.eligibleVariantIds);
  const compared = candidate.product.variants.filter((variant) => eligibleIds.has(String(variant.id)))
    .sort((a, b) => a.compareAtPrice - b.compareAtPrice);
  if (!compared.length || compared.length !== eligibleIds.size
    || compared.some((variant) => !Number.isSafeInteger(variant.compareAtPrice) || variant.compareAtPrice <= variant.price)) {
    throw new Error('CARD_PRICE_INCOMPLETE');
  }
  const regularLabel = document.createElement('span');
  regularLabel.className = 'visually-hidden';
  regularLabel.textContent = `${price.dataset.regularLabel} `;
  const regularPrice = document.createElement('s');
  regularPrice.className = 'compare-at-price';
  regularPrice.append(regularLabel, formatted(String(compared[0].id), compared[0].compareAtPrice, true));
  if (compared[0].compareAtPrice !== compared.at(-1).compareAtPrice) {
    regularPrice.append(' – ', formatted(String(compared.at(-1).id), compared.at(-1).compareAtPrice, true));
  }
  price.append(' ', regularPrice);
  price.hidden = false;
  for (const template of [...templates, ...compareTemplates]) template.remove();
}

async function fetchCatalog(cartUrl, signal, sectionId = 'br-sale-picks-catalog') {
  let first;
  const products = new Map();
  for (let page = 1; ; page++) {
    const doc = await fetchSection(cartUrl, sectionId, signal, page);
    const data = parsePayload(doc, '[data-br-sale-picks-catalog]');
    if (data.enabled !== true) return null;
    if (!Number.isSafeInteger(data.pages) || data.pages < 1 || data.pages > 250 || data.page !== page
      || !Number.isSafeInteger(data.total) || data.total < 0 || data.total > 25000
      || !Array.isArray(data.lines) || !Array.isArray(data.products)
      || !Number.isSafeInteger(data.lineCount) || data.lineCount !== data.lines.length) throw new Error('CATALOG_INCOMPLETE');
    if (!first) first = data;
    if (data.collectionId !== first.collectionId || data.pages !== first.pages || data.total !== first.total
      || data.priorityCollectionId !== first.priorityCollectionId
      || cartSignature(data.lines) !== cartSignature(first.lines)
      || JSON.stringify(data.contract) !== JSON.stringify(first.contract)) throw new Error('CATALOG_CHANGED');
    for (const product of data.products) {
      if (products.has(product.id)) throw new Error('CATALOG_CHANGED');
      products.set(product.id, product);
    }
    if (page === data.pages) break;
  }
  if (products.size !== first.total) throw new Error('CATALOG_INCOMPLETE');
  return { ...first, products: [...products.values()] };
}

function display(mount, view) {
  const focused = mount.contains(document.activeElement);
  const focusedProduct = focused ? document.activeElement.closest('[data-product-id]')?.dataset.productId : null;
  mount.querySelector('[data-br-sale-picks-view]')?.remove();
  const skeleton = mount.querySelector('[data-br-sale-picks-loading]');
  if (skeleton) skeleton.hidden = true;
  mount.setAttribute('aria-busy', 'false');
  mount.style.minHeight = '';
  if (view) {
    const clone = view.cloneNode(true);
    mount.append(clone);
    mount.hidden = false;
    if (focused) {
      const card = [...mount.querySelectorAll('[data-product-id]')].find((node) => node.dataset.productId === focusedProduct);
      (card?.querySelector('button') ?? mount.querySelector('[data-br-sale-picks-view] h3'))?.focus({ preventScroll: true });
    }
  } else {
    mount.hidden = true;
    if (focused) document.querySelector('#cart-drawer .theme-drawer__close-button')?.focus({ preventScroll: true });
  }
  mount.scheduleJumpUpdate();
}

function displayLoading(mount) {
  const focused = mount.contains(document.activeElement);
  const view = mount.querySelector('[data-br-sale-picks-view]');
  if (view) mount.style.minHeight = `${view.getBoundingClientRect().height}px`;
  view?.remove();
  const skeleton = mount.querySelector('[data-br-sale-picks-loading]');
  if (skeleton) skeleton.hidden = false;
  mount.hidden = false;
  mount.setAttribute('aria-busy', 'true');
  if (focused) skeleton?.querySelector('h3')?.focus({ preventScroll: true });
  mount.scheduleJumpUpdate();
}

function beginLoading() {
  loading = true;
  for (const mount of mounts) displayLoading(mount);
}

function publish(view) {
  loading = false;
  savedView = view;
  for (const mount of mounts) display(mount, view);
}

async function refresh(mount) {
  if (!mount.isConnected || (!mount.closest('.br-cart-page') && !mount.closest('#cart-drawer')?.hasAttribute('open')) || pendingMutations) return;
  if (!dirty) {
    if (!mount.querySelector('[data-br-sale-picks-view]')) display(mount, savedView);
    return;
  }
  if (activeRequest) {
    displayLoading(mount);
    return;
  }
  beginLoading();
  const controller = new AbortController();
  activeRequest = controller;
  const version = generation;
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const catalog = await fetchCatalog(mount.dataset.cartUrl, controller.signal);
    let view;
    const ids = [];
    if (catalog) {
      const result = rankSalePicks({ ...catalog, previousIds: preserveOrder ? previousIds : [] });
      const groups = [];
      const diagnostics = [...result.diagnostics];
      if (catalog.priorityCollectionId) {
        const priority = await fetchCatalog(mount.dataset.cartUrl, controller.signal, 'br-sale-picks-priority-catalog');
        if (!priority || priority.collectionId !== catalog.priorityCollectionId
          || cartSignature(priority.lines) !== cartSignature(catalog.lines)
          || JSON.stringify(priority.contract) !== JSON.stringify(catalog.contract)) throw new Error('CATALOG_CHANGED');
        const priorityResult = rankPriorityCollection({ ...priority, previousIds: preserveOrder ? previousIds : [] });
        groups.push({ result: priorityResult, catalog: priority, rank: rankPriorityCollection, limit: 3 });
        diagnostics.push(...priorityResult.diagnostics);
      }
      groups.push({ result, catalog, rank: rankSalePicks, limit: 6 });
      mount.dispatchEvent(new CustomEvent('br:sale-picks-diagnostics', { detail: diagnostics }));
      const shell = mount.querySelector('[data-br-sale-picks-shell]').content.cloneNode(true);
      const section = shell.querySelector('section');
      const scroller = shell.querySelector('slideshow-slides');
      const displayLimit = Math.max(1, Math.min(6, Number(mount.dataset.maxProducts) || 6));
      for (const group of groups) {
        for (const candidate of group.result.ranked) {
          if (ids.length >= Math.min(group.limit, displayLimit)) break;
          if (ids.some((id) => String(id) === String(candidate.product.id))) continue;
          const doc = await fetchSection(candidate.product.url, 'br-sale-picks-card', controller.signal);
          const dataNode = doc.querySelector('[data-br-sale-picks-product]');
          const template = doc.querySelector('[data-br-sale-picks-card]');
          if (!dataNode || !template) continue;
          const fresh = JSON.parse(dataNode.dataset.json);
          if (String(fresh.id) !== String(candidate.product.id)) throw new Error('CARD_MISMATCH');
          const rechecked = group.rank({ ...group.catalog, products: [fresh] }).ranked[0];
          if (!rechecked) continue;
          if (rechecked.rank !== candidate.rank || rechecked.printRank !== candidate.printRank) throw new Error('CATALOG_CHANGED');
          const slide = template.content.querySelector('slideshow-slide')?.cloneNode(true);
          if (!slide || String(slide.querySelector('[data-product-id]')?.dataset.productId) !== String(fresh.id)) {
            throw new Error('CARD_MISMATCH');
          }
          applyEligiblePrice(slide, rechecked);
          slide.setAttribute('aria-hidden', ids.length === 0 ? 'false' : 'true');
          slide.style.setProperty('--slideshow-timeline', `--slide-${ids.length}`);
          scroller.append(slide);
          ids.push(fresh.id);
          if (ids.length === group.limit) break;
        }
      }
      if (ids.length) {
        section.dataset.brSalePicksView = '';
        view = section;
      }
    }
    if (version !== generation || pendingMutations || controller.signal.aborted) return;
    previousIds = ids;
    dirty = false;
    publish(view);
  } catch (error) {
    if (version === generation && !pendingMutations) {
      publish(undefined);
      mount.dispatchEvent(new CustomEvent('br:sale-picks-diagnostics', { detail: [{ code: error.name === 'AbortError' ? 'REQUEST_ABORTED' : error.message }] }));
    }
  } finally {
    clearTimeout(timeout);
    if (activeRequest === controller) activeRequest = undefined;
  }
}

class MiniCartSalePicks extends HTMLElement {
  observer;
  resizeObserver;
  drawer;
  jumpFrame;

  scheduleJumpUpdate = () => {
    if (this.jumpFrame) return;
    this.jumpFrame = requestAnimationFrame(() => {
      this.jumpFrame = undefined;
      this.updateJump();
    });
  };

  updateJump() {
    const jump = this.drawer?.querySelector('[data-br-sale-picks-jump]');
    if (!jump) return;
    let bottom = window.innerHeight;
    let canScroll = false;
    for (let parent = this.parentElement; parent && parent !== this.drawer; parent = parent.parentElement) {
      const overflow = getComputedStyle(parent).overflowY;
      if (/(auto|scroll|hidden|clip)/.test(overflow)) {
        bottom = Math.min(bottom, parent.getBoundingClientRect().top + parent.clientTop + parent.clientHeight);
      }
      if (/(auto|scroll)/.test(overflow) && parent.scrollHeight - parent.clientHeight - parent.scrollTop > 8) canScroll = true;
    }
    const summary = this.drawer.querySelector('.cart-drawer__summary');
    if (summary && getComputedStyle(summary).position === 'sticky') bottom = Math.min(bottom, summary.getBoundingClientRect().top);
    // The hint occupies its own row: never cover product images, prices or controls.
    // A visible heading is enough; the entire carousel need not fit above checkout.
    const heading = this.querySelector('[data-br-sale-picks-view] h3, [data-br-sale-picks-loading]:not([hidden]) h3');
    const headingVisible = heading && heading.getBoundingClientRect().bottom <= bottom;
    jump.hidden = this.hidden || !this.drawer.hasAttribute('open') || !canScroll || headingVisible;
  }

  handleJump = (event) => {
    if (!event.target.closest?.('[data-br-sale-picks-jump-link]') || this.hidden) return;
    event.preventDefault();
    const heading = this.querySelector('[data-br-sale-picks-view] h3, [data-br-sale-picks-loading]:not([hidden]) h3');
    heading?.focus({ preventScroll: true });
    this.scrollIntoView({ block: 'start', inline: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  connectedCallback() {
    mounts.add(this);
    this.drawer = this.closest('#cart-drawer');
    this.drawer?.addEventListener('scroll', this.scheduleJumpUpdate, true);
    this.drawer?.addEventListener('click', this.handleJump);
    window.addEventListener('resize', this.scheduleJumpUpdate);
    this.resizeObserver = new ResizeObserver(this.scheduleJumpUpdate);
    for (const node of [this, this.closest('.cart-drawer__items'), this.closest('.cart-drawer__content'), this.drawer?.querySelector('.cart-drawer__summary')]) {
      if (node) this.resizeObserver.observe(node);
    }
    this.observer = new MutationObserver(() => {
      if (loading) displayLoading(this);
      else if (!dirty && savedView && !this.querySelector('[data-br-sale-picks-view]')) display(this, savedView);
      this.scheduleJumpUpdate();
    });
    this.observer.observe(this, { childList: true });
    if (loading || pendingMutations) displayLoading(this);
    else if (!dirty) display(this, savedView);
    queueMicrotask(() => refresh(this));
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.drawer?.removeEventListener('scroll', this.scheduleJumpUpdate, true);
    this.drawer?.removeEventListener('click', this.handleJump);
    window.removeEventListener('resize', this.scheduleJumpUpdate);
    cancelAnimationFrame(this.jumpFrame);
    this.jumpFrame = undefined;
    mounts.delete(this);
  }
}

if (!customElements.get('br-mini-cart-sale-picks')) customElements.define('br-mini-cart-sale-picks', MiniCartSalePicks);

document.addEventListener(DrawerOpenEvent.eventName, (event) => {
  if (event.target?.id !== 'cart-drawer') return;
  dirty = true;
  preserveOrder = true;
  for (const mount of mounts) refresh(mount);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise?.then) return;
  pendingMutations++;
  generation++;
  activeRequest?.abort();
  activeRequest = undefined;
  beginLoading();
  event.promise.then((result) => {
    if (result?.detail?.didError === false) {
      dirty = true;
      preserveOrder = event.action !== 'add';
      savedView = undefined;
    }
  }).catch(() => {}).finally(() => {
    pendingMutations--;
    if (!pendingMutations) requestAnimationFrame(() => {
      if (pendingMutations) return;
      if (!dirty) publish(savedView);
      for (const mount of mounts) refresh(mount);
    });
  });
});
